import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPopper } from '@popperjs/core';
// Components
import { Icons, SVG } from '../../../../atoms/SVG';
import FormMultiselectPanel from './FormMultiselectPanel/FormMultiselectPanel';
import { Namespace } from '../../../../../translation/namespace';
import bem from '../../../../../lib/bemUtils/bem';
import { CollapsibleContent } from '../../../../organisms/CollapsibleOverlay/CollapsibleOverlay';
import { CollapsibleModifier } from '../../../../organisms/CollapsibleOverlay/CollapsibleOverlay.modifier';
import useCollapsibleOverlay from '../../../../../hooks/useCollapsibleOverlay';
import { StateFacetItem } from '../../../ResultListSettings/ResultListSettings';
import { HeadingHierarchy } from '../../../../../_lib/components/Heading/Heading';

type FormMultiselectType = {
  title: string;
  name: string;
  buttonHeadline?: string;
  hierarchy?: HeadingHierarchy;
  showButtons?: boolean;
  focusOnMount?: boolean;
  items?: StateFacetItem[];
  invertedButtons?: boolean;
  disableSubmitButton?: boolean;
  darkMode?: boolean;
  // Events
  onSubmit?: any;
  onChange?: any;
  // for Resultlistsettings where this is not handled by formmodel
  onCloseResetFacet?: any;
  sortAphabetically?: boolean;
  modifier?: FormMultiselectModifier;
};

export enum FormMultiselectModifier {
  ROLE_CHANGE_FORM = 'role-change-form',
  WIDTH_100 = 'width-100',
}

/**
 * FormMultiselect
 */
export default function FormMultiselect({
  buttonHeadline,
  darkMode = false,
  focusOnMount = false,
  hierarchy,
  invertedButtons = false,
  disableSubmitButton = false,
  items = [],
  name,
  onChange,
  onSubmit,
  showButtons = false,
  title,
  onCloseResetFacet,
  sortAphabetically = false,
  modifier,
}: FormMultiselectType) {
  const { t } = useTranslation();
  const { collapsibleOpen, focusToggle, toggleCollapsible, toggleProps, collapsibleProps } = useCollapsibleOverlay();

  // Creating snapshot of selection "on load" to restore this on reset
  const initialSnapshotRef = useRef(JSON.parse(JSON.stringify(items)));

  // We have to parse it, in other case dynamicItems gets same reference
  const [dynamicItems, setDynamicItems] = useState(JSON.parse(JSON.stringify(items)));

  const getActiveItems = () =>
    showButtons ? items.filter(item => item.active) : dynamicItems.filter((item: StateFacetItem) => item.active);
  const hasActiveItems = () => getActiveItems().length > 0;

  /**
   * Returns the Containers-Title according to the active items.
   */
  const getActiveItemsTitle = () => {
    const activeItems = getActiveItems();
    switch (activeItems.length) {
      case 0:
        return '';
      case 1:
        return `${activeItems[0].text}`;
      default:
        return `${activeItems.length} ${t(`${Namespace.glossary}:active`)}`;
    }
  };

  // is held in state, so it won't change while we select/deselect items
  const [activeItemTitle, setActiveItemTitle] = useState(getActiveItemsTitle());

  /**
   * Filter change's event handler
   * Mutliselects without submit buttons show dynamic change of button title, when options are selected.
   * @param event single check box event providing data what to add to filter
   */
  const handleOnFilterChange = (event: any) => {
    if (onChange) {
      onChange(event);
    }
    const currentDynamicItems = JSON.parse(JSON.stringify(dynamicItems));
    const index = currentDynamicItems.findIndex((item: StateFacetItem) => item.value === event.target.value);
    currentDynamicItems[index].active = event.target.checked;
    setDynamicItems(currentDynamicItems);
  };

  /**
   * Submit button's event handler
   * @param event
   */
  const handleOnSubmit = (event: any) => {
    if (onSubmit) {
      onSubmit(event, getActiveItems(), name);
    }
  };

  const handleOnReset = () => {
    setDynamicItems(initialSnapshotRef.current);
    toggleCollapsible();
    focusToggle();
  };

  /**
   *  change button title onChange only if MultiSelect can't be submitted (no submit buttons here)
   */
  useEffect(() => {
    if (!showButtons) {
      setActiveItemTitle(getActiveItemsTitle());
    }
  }, [dynamicItems]);

  /**
   * handle rendering of collapsible via popper
   */
  useEffect(() => {
    let popper: any;

    if (collapsibleOpen) {
      // using timeout here to tackle a race condition between popper an the Browser's popover API
      setTimeout(() => {
        popper = createPopper(
          toggleProps.ref.current as any,
          document.getElementById(collapsibleProps.id) as HTMLElement,
          {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 1],
                },
              },
            ],
          },
        );
      }, 0);
    }

    // special case where values need to be rest.
    // not handled by formModel magic -> see ResultListSettings.
    if (!collapsibleOpen && onCloseResetFacet) {
      onCloseResetFacet();
    }

    return () => {
      if (popper) {
        popper.destroy();
      }
    };
    // onCloseResetFacet is not needed here, else it cant be resetted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsibleOpen, toggleProps.ref]);

  // onMount
  useEffect(() => {
    if (focusOnMount) {
      focusToggle();
    }
    // only want on mount, so it's ok here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const classArray = () => {
    let classes = [];
    if (modifier) {
      classes.push(modifier);
    }
    if (darkMode) {
      classes.push('facet');
    }
    return classes;
  };

  return (
    <div className={bem('form-multiselect', classArray())}>
      <button
        type="button"
        {...toggleProps}
        aria-expanded={undefined} // resetting aria expanded here, because it's invalid together with popover
        className={`form-multiselect__button ${hasActiveItems() ? 'form-multiselect__button--active' : ''}`}
        popoverTarget={collapsibleProps.id}
        popoverTargetAction="toggle"
        // Usually, there are items. In some cases, backend has none, so we disable the button, see SON-19586
        disabled={!items || items.length === 0 ? true : undefined}
      >
        <span className="form-multiselect__title-container">
          <span className="form-multiselect__title">
            {title}
            {activeItemTitle ? ': ' : ''}
          </span>
          <span className="form-multiselect__value">{activeItemTitle}</span>
        </span>
        <span className="form-multiselect__icon-container">
          <SVG svgSpriteId={Icons.ARROW_DOWN} svgClass="form-multiselect__icon" />
        </span>
      </button>
      <CollapsibleContent
        {...collapsibleProps}
        role="dialog"
        aria-label={title}
        popover="auto"
        modifier={darkMode ? CollapsibleModifier.FORM_MULTISELECT_FACET : CollapsibleModifier.FORM_MULTISELECT}
        returnChildrenWhenClosed
      >
        <FormMultiselectPanel
          name={name}
          items={dynamicItems}
          buttonHeadline={buttonHeadline}
          hierarchy={hierarchy}
          showButtons={showButtons}
          controls={name}
          darkMode={darkMode}
          invertedButtons={invertedButtons}
          disableSubmitButton={disableSubmitButton}
          // Events
          onSubmit={handleOnSubmit}
          onChange={handleOnFilterChange}
          onReset={handleOnReset}
          sortAlphabetically={sortAphabetically}
        />
      </CollapsibleContent>
    </div>
  );
}
